import React, { useState } from 'react';
import { LiquidityEvent, Currency } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { PlusCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { CURRENCIES } from '../utils/countries';
interface LiquidityEventFormProps {
  onAdd: (event: Omit<LiquidityEvent, 'id'>) => void;
  baseCurrency?: Currency;
  initialValues?: {
    name?: string;
    currency?: Currency;
    type?: LiquidityEvent['type'];
    isPositive?: boolean;
    startYear?: string;
  };
}
export function LiquidityEventForm({
  onAdd,
  baseCurrency = 'USD',
  initialValues
}: LiquidityEventFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(initialValues?.currency ?? baseCurrency);
  const [type, setType] = useState<LiquidityEvent['type']>(initialValues?.type ?? 'one-time');
  const [startYear, setStartYear] = useState(initialValues?.startYear ?? '1');
  const [endYear, setEndYear] = useState('');
  const [isPositive, setIsPositive] = useState(initialValues?.isPositive ?? true);
  const [annualIncrease, setAnnualIncrease] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount || isNaN(Number(amount))) return;
    if (!startYear || isNaN(Number(startYear))) return;
    const finalAmount = Number(amount) * (isPositive ? 1 : -1);
    const increaseRate = annualIncrease ? Number(annualIncrease) : undefined;
    onAdd({
      name,
      amount: finalAmount,
      currency,
      type,
      startYear: Number(startYear),
      endYear: endYear ? Number(endYear) : undefined,
      annualIncreaseRate: type !== 'one-time' && increaseRate ? increaseRate : undefined
    });
    setName('');
    setAmount('');
    setStartYear('1');
    setEndYear('');
    setAnnualIncrease('');
  };
  return <Card data-id="element-709">
      <CardHeader title="Add Liquidity Event" subtitle="Future cash flows (e.g. property sale, inheritance, tuition)" data-id="element-710" />
      <CardContent data-id="element-711">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-712">
          <div data-id="element-713">
            <Label data-id="element-714">Event Name</Label>
            <Input placeholder="e.g. Sell property, College tuition" value={name} onChange={e => setName(e.target.value)} required data-id="element-715" />
          </div>

          <div data-id="element-716">
            <Label data-id="element-717">Direction</Label>
            <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-718">
              <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${isPositive ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsPositive(true)} data-id="element-719">
                <ArrowUpRight className="w-4 h-4" data-id="element-720" /> Inflow (+)
              </button>
              <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${!isPositive ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsPositive(false)} data-id="element-721">
                <ArrowDownRight className="w-4 h-4" data-id="element-722" /> Outflow (-)
              </button>
            </div>
          </div>

          <div data-id="element-723">
            <Label data-id="element-724">Frequency</Label>
            <Select value={type} onChange={e => setType(e.target.value as LiquidityEvent['type'])} options={[{
            value: 'one-time',
            label: 'One-time'
          }, {
            value: 'monthly',
            label: 'Monthly'
          }, {
            value: 'yearly',
            label: 'Yearly'
          }]} data-id="element-725" />
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-726">
            <div className="col-span-2" data-id="element-727">
              <Label data-id="element-728">Amount</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required data-id="element-729" />
            </div>
            <div data-id="element-730">
              <Label data-id="element-731">Currency</Label>
              <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
              value: c.code,
              label: `${c.flag} ${c.code} (${c.symbol})`
            }))} data-id="element-732" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-733">
            <div data-id="element-734">
              <Label data-id="element-735">Start Year (from now)</Label>
              <Input type="number" min="0" step="1" value={startYear} onChange={e => setStartYear(e.target.value)} required data-id="element-736" />
            </div>
            {type !== 'one-time' && <div data-id="element-737">
                <Label data-id="element-738">End Year (optional)</Label>
                <Input type="number" min={startYear} step="1" placeholder="Forever" value={endYear} onChange={e => setEndYear(e.target.value)} data-id="element-739" />
              </div>}
          </div>

          <div data-id="element-740">
            <Label data-id="element-741">Annual Increase % (optional)</Label>
            <Input type="number" min="0" max="50" step="0.1" placeholder={type === 'one-time' ? 'N/A for one-time events' : 'e.g. 3 for 3% yearly growth'} value={annualIncrease} onChange={e => setAnnualIncrease(e.target.value)} disabled={type === 'one-time'} data-id="element-742" />
            <p className="text-xs text-slate-400 mt-1" data-id="element-743">
              {type === 'one-time' ? 'Switch to Monthly or Yearly to enable annual growth.' : 'The amount will grow by this percentage each year from the start year.'}
            </p>
          </div>

          <Button type="submit" className="w-full mt-2" data-id="element-744">
            <PlusCircle className="w-4 h-4 mr-2" data-id="element-745" />
            Add Event
          </Button>
        </form>
      </CardContent>
    </Card>;
}